import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { downloadStudentLorFile } from 'api/lorRequests'
import {
	createCounselorLorGeneration,
	downloadCounselorLorPdf,
	fetchCounselorLorContext,
	fetchCounselorLorJob,
	fetchCounselorLorLetter,
	patchCounselorLorLetter,
	retryCounselorLorJob,
} from 'api/counselorLor'

const DEFAULT_TONES = ['Warm', 'Formal', 'Academic', 'Confident', 'Detailed', 'Concise']
const DEFAULT_HIGHLIGHTS = [
	'Leadership',
	'Initiative',
	'Responsibility',
	'Integrity',
	'Maturity',
	'Independence',
	'Adaptability',
	'Resilience',
	'Work Ethic',
	'Motivation',
	'Personal Growth',
	'Self-Awareness',
	'Creativity',
	'Passion',
	'Confidence',
	'Academic Excellence',
	'Academic Potential',
	'Intellectual Curiosity',
	'Research Experience',
	'Analytical Thinking',
]

const INITIAL_POLL_MS = 2000
const MAX_POLL_MS = 5000

const normalizeApplication = (application, fallbackStudent = null) => {
	if (!application) return null
	const applicationTargetId = String(
		application.applicationTargetId || application.id || '',
	).trim()
	const studentId = String(
		application.studentId || fallbackStudent?.id || '',
	).trim()
	if (!applicationTargetId || !studentId) return null

	return {
		applicationTargetId,
		studentId,
		studentName: String(
			application.studentName || fallbackStudent?.name || '',
		).trim(),
		universityId: application.universityId || null,
		university: String(
			application.university || application.universityName || '',
		).trim(),
		program: String(
			application.program || application.major || application.courseName || '',
		).trim(),
		intake: String(
			application.intake ||
				application.intakeSession ||
				application.intake_session ||
				'',
		).trim(),
	}
}

const createIdempotencyKey = () => {
	if (typeof crypto !== 'undefined' && crypto.randomUUID) {
		return crypto.randomUUID()
	}
	return `lor-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const wait = (ms, signal) =>
	new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(signal.reason || new DOMException('Aborted', 'AbortError'))
			return
		}
		const timeoutId = window.setTimeout(resolve, ms)
		const onAbort = () => {
			window.clearTimeout(timeoutId)
			reject(signal?.reason || new DOMException('Aborted', 'AbortError'))
		}
		signal?.addEventListener('abort', onAbort, { once: true })
	})

const formatReferenceCard = reference => {
	const university = reference?.university || ''
	const program = reference?.program || reference?.subtitle || ''
	const title =
		reference?.title ||
		(university ? `LoR — ${university}` : 'Letter of Recommendation')
	const dateValue = reference?.updatedAt || reference?.generatedAt
	let date = ''
	if (dateValue) {
		const parsed = new Date(dateValue)
		if (!Number.isNaN(parsed.getTime())) {
			date = parsed.toLocaleDateString('en-US', {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			})
		}
	}
	return {
		id: reference.id,
		title,
		subtitle: program || university || 'Previous letter',
		author:
			reference.authorName ||
			reference.authorDisplayName ||
			reference.authorCounselorName ||
			'Counselor',
		date,
	}
}

export const useGenerateLorModal = ({
	isOpen,
	onClose,
	application,
	onLetterSaved,
	initialLetter = null,
}) => {
	const [selectedApplication, setSelectedApplication] = useState(null)
	const [context, setContext] = useState(null)
	const [contextStatus, setContextStatus] = useState('idle')
	const [contextError, setContextError] = useState('')

	const [instructions, setInstructions] = useState('')
	const [tones, setTones] = useState([])
	const [highlights, setHighlights] = useState([])
	const [showAllHighlights, setShowAllHighlights] = useState(false)
	const [showAllReferences, setShowAllReferences] = useState(false)
	const [useBragSheet, setUseBragSheet] = useState(true)
	const [useProfileBuilder, setUseProfileBuilder] = useState(true)
	const [selectedReferenceId, setSelectedReferenceId] = useState('')

	const [isAppPopoverOpen, setIsAppPopoverOpen] = useState(false)
	const [composerVisible, setComposerVisible] = useState(true)
	const [pdfPreview, setPdfPreview] = useState({ open: false, url: '', title: '' })
	const [isPreviewLoading, setIsPreviewLoading] = useState(false)
	const previewAbortRef = useRef(null)

	const [mode, setMode] = useState('new')
	const [letterId, setLetterId] = useState(null)
	const [jobId, setJobId] = useState(null)
	const [letter, setLetter] = useState(null)
	const [writingStatus, setWritingStatus] = useState('idle')
	const [generationError, setGenerationError] = useState('')
	const [drawerMode, setDrawerMode] = useState('view')
	const [savedPillVisible, setSavedPillVisible] = useState(false)
	const [toast, setToast] = useState({ isVisible: false, message: '', type: 'success' })

	const contextRequestRef = useRef(0)
	const pollAbortRef = useRef(null)
	const idempotencyKeyRef = useRef(null)
	const editorFlushRef = useRef(null)

	const studentId = selectedApplication?.studentId || ''
	const applicationTargetId = selectedApplication?.applicationTargetId || ''

	const tonesList = context?.allowedTones?.length ? context.allowedTones : DEFAULT_TONES
	const highlightsList = context?.allowedHighlights?.length
		? context.allowedHighlights
		: DEFAULT_HIGHLIGHTS
	const applications = context?.applications || []
	const references = useMemo(
		() => (context?.references || []).map(formatReferenceCard),
		[context],
	)
	const bragSheet = context?.bragSheet || { available: false }
	const profileBuilder = context?.profileBuilder || {
		completionPercent: 0,
		usable: false,
	}

	const resetComposerForm = useCallback(() => {
		setInstructions('')
		setTones([])
		setHighlights([])
		setShowAllHighlights(false)
		setShowAllReferences(false)
		setUseBragSheet(true)
		setUseProfileBuilder(true)
		setSelectedReferenceId('')
		setMode('new')
		setLetterId(null)
		setJobId(null)
		setLetter(null)
		setWritingStatus('idle')
		setGenerationError('')
		setDrawerMode('view')
		setComposerVisible(true)
		setSavedPillVisible(false)
		setIsAppPopoverOpen(false)
		idempotencyKeyRef.current = null
	}, [])

	// Reset generation UI before next open paint so a stale "success" drawer
	// does not flash when reopening (e.g. Improve with AI from parent drawer).
	useLayoutEffect(() => {
		if (isOpen) return
		setWritingStatus('idle')
		setGenerationError('')
		setComposerVisible(true)
		setDrawerMode('view')
		setSavedPillVisible(false)
		if (pollAbortRef.current) {
			pollAbortRef.current.abort()
			pollAbortRef.current = null
		}
	}, [isOpen])

	const applySettingsFromLetter = useCallback(
		(settings, nextContext) => {
			const nextSettings = settings || {}
			setInstructions(String(nextSettings.instructions || ''))
			setTones(Array.isArray(nextSettings.tones) ? nextSettings.tones : [])
			setHighlights(
				Array.isArray(nextSettings.highlights) ? nextSettings.highlights : [],
			)

			const bragAvailable = Boolean(nextContext?.bragSheet?.available)
			const profileUsable = Boolean(nextContext?.profileBuilder?.usable)
			const referenceIds = new Set(
				(nextContext?.references || []).map(item => String(item.id)),
			)
			const savedReference = String(nextSettings.referenceLetterId || '')

			if (bragAvailable) {
				setUseBragSheet(
					nextSettings.useBragSheet === undefined
						? true
						: Boolean(nextSettings.useBragSheet),
				)
				setUseProfileBuilder(false)
			} else if (profileUsable) {
				setUseBragSheet(false)
				setUseProfileBuilder(
					nextSettings.useProfileBuilder === undefined
						? true
						: Boolean(nextSettings.useProfileBuilder),
				)
			} else {
				setUseBragSheet(false)
				setUseProfileBuilder(false)
			}

			setSelectedReferenceId(
				savedReference && referenceIds.has(savedReference) ? savedReference : '',
			)
		},
		[],
	)

	const initializeTogglesFromContext = useCallback(nextContext => {
		const bragAvailable = Boolean(nextContext?.bragSheet?.available)
		const profileUsable = Boolean(nextContext?.profileBuilder?.usable)
		setUseBragSheet(bragAvailable)
		setUseProfileBuilder(!bragAvailable && profileUsable)
		setSelectedReferenceId('')
		setShowAllReferences(false)
	}, [])

	const loadContext = useCallback(
		async ({
			studentId: nextStudentId,
			applicationTargetId: nextApplicationTargetId,
			preserveSelections = false,
			excludeLetterId = null,
			prefillSettings = null,
		}) => {
			if (!nextStudentId || !nextApplicationTargetId) {
				setContextStatus('error')
				setContextError('Student and application are required.')
				return
			}

			const requestId = contextRequestRef.current + 1
			contextRequestRef.current = requestId
			const abortController = new AbortController()
			setContextStatus('loading')
			setContextError('')

			try {
				const data = await fetchCounselorLorContext(
					nextStudentId,
					nextApplicationTargetId,
					{
						excludeLetterId,
						signal: abortController.signal,
					},
				)
				if (requestId !== contextRequestRef.current) return

				setContext(data)
				const selected =
					normalizeApplication(data.application, data.student) ||
					normalizeApplication({
						...selectedApplication,
						applicationTargetId: nextApplicationTargetId,
						studentId: nextStudentId,
					})
				if (selected) {
					setSelectedApplication(prev => ({
						...selected,
						studentName: selected.studentName || prev?.studentName || '',
					}))
				}

				if (prefillSettings) {
					applySettingsFromLetter(prefillSettings, data)
				} else if (!preserveSelections) {
					initializeTogglesFromContext(data)
				} else {
					initializeTogglesFromContext(data)
				}

				if (data.activeGeneration?.letterId && data.activeGeneration?.jobId) {
					setLetterId(data.activeGeneration.letterId)
					setJobId(data.activeGeneration.jobId)
					setWritingStatus('writing')
				}

				setContextStatus('ready')
			} catch (error) {
				if (error?.name === 'CanceledError' || error?.name === 'AbortError') return
				if (requestId !== contextRequestRef.current) return
				setContextStatus('error')
				setContextError(
					error?.response?.data?.error ||
						error?.message ||
						'Failed to load LoR context',
				)
			}

			return () => abortController.abort()
		},
		[applySettingsFromLetter, initializeTogglesFromContext, selectedApplication],
	)

	useEffect(() => {
		if (!isOpen) {
			setWritingStatus('idle')
			setComposerVisible(true)
			setIsAppPopoverOpen(false)
			setIsPreviewLoading(false)
			if (previewAbortRef.current) {
				previewAbortRef.current.abort()
				previewAbortRef.current = null
			}
			setPdfPreview(current => {
				if (current.url) URL.revokeObjectURL(current.url)
				return { open: false, url: '', title: '' }
			})
			if (pollAbortRef.current) {
				pollAbortRef.current.abort()
				pollAbortRef.current = null
			}
			return undefined
		}

		const normalized = normalizeApplication(application)
		if (!normalized) {
			setContextStatus('error')
			setContextError('Student and application are required.')
			return undefined
		}

		resetComposerForm()
		setSelectedApplication(normalized)

		if (initialLetter?.id) {
			setMode('improve')
			setLetterId(initialLetter.id)
			setLetter(initialLetter)
			loadContext({
				studentId: normalized.studentId,
				applicationTargetId:
					initialLetter.applicationTargetId || normalized.applicationTargetId,
				excludeLetterId: initialLetter.id,
				prefillSettings: initialLetter.lastSettings || {},
			})
		} else {
			loadContext({
				studentId: normalized.studentId,
				applicationTargetId: normalized.applicationTargetId,
			})
		}

		const previousOverflow = document.body.style.overflow
		const handleKeyDown = event => {
			if (event.key === 'Escape') {
				if (isAppPopoverOpen) {
					setIsAppPopoverOpen(false)
					return
				}
				if (writingStatus === 'idle' && composerVisible) onClose()
			}
		}

		document.body.style.overflow = 'hidden'
		document.addEventListener('keydown', handleKeyDown)

		return () => {
			document.body.style.overflow = previousOverflow
			document.removeEventListener('keydown', handleKeyDown)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		isOpen,
		application?.id,
		application?.applicationTargetId,
		application?.studentId,
		initialLetter?.id,
	])

	const changeInstructions = useCallback(event => {
		setInstructions(event.target.value)
	}, [])

	const toggleTone = useCallback(value => {
		setTones(current =>
			current.includes(value)
				? current.filter(item => item !== value)
				: [...current, value],
		)
	}, [])

	const toggleHighlight = useCallback(value => {
		setHighlights(current =>
			current.includes(value)
				? current.filter(item => item !== value)
				: [...current, value],
		)
	}, [])

	const toggleHighlightsVisibility = useCallback(() => {
		setShowAllHighlights(current => !current)
	}, [])

	const toggleReferencesVisibility = useCallback(() => {
		setShowAllReferences(current => !current)
	}, [])

	const toggleBragSheet = useCallback(event => {
		setUseBragSheet(event.target.checked)
	}, [])

	const toggleProfileBuilder = useCallback(event => {
		setUseProfileBuilder(event.target.checked)
	}, [])

	const selectReference = useCallback(value => {
		setSelectedReferenceId(current => (current === value ? '' : value))
	}, [])

	const stopPropagation = useCallback(event => {
		event.stopPropagation()
	}, [])

	const changeApplication = useCallback(() => {
		if (mode === 'improve') return
		setIsAppPopoverOpen(current => !current)
	}, [mode])

	const selectApplication = useCallback(
		async nextApplication => {
			if (mode === 'improve') return
			const normalized = normalizeApplication(nextApplication, {
				id: studentId,
				name: selectedApplication?.studentName,
			})
			if (!normalized) return
			if (normalized.applicationTargetId === applicationTargetId) {
				setIsAppPopoverOpen(false)
				return
			}

			setIsAppPopoverOpen(false)
			setSelectedApplication(normalized)
			setContext(null)
			await loadContext({
				studentId: normalized.studentId,
				applicationTargetId: normalized.applicationTargetId,
				preserveSelections: true,
			})
		},
		[
			applicationTargetId,
			loadContext,
			mode,
			selectedApplication?.studentName,
			studentId,
		],
	)

	const closePdfPreview = useCallback(() => {
		if (previewAbortRef.current) {
			previewAbortRef.current.abort()
			previewAbortRef.current = null
		}
		setIsPreviewLoading(false)
		setPdfPreview(current => {
			if (current.url) URL.revokeObjectURL(current.url)
			return { open: false, url: '', title: '' }
		})
		setComposerVisible(true)
	}, [])

	const preview = useCallback(async () => {
		const fileId = bragSheet?.fileId
		if (!studentId || !fileId || isPreviewLoading) return

		if (previewAbortRef.current) {
			previewAbortRef.current.abort()
		}
		const abortController = new AbortController()
		previewAbortRef.current = abortController

		setIsPreviewLoading(true)
		setIsAppPopoverOpen(false)
		setComposerVisible(false)
		setPdfPreview(current => {
			if (current.url) URL.revokeObjectURL(current.url)
			return {
				open: true,
				url: '',
				title: 'Brag Sheet',
			}
		})

		try {
			const blob = await downloadStudentLorFile(studentId, fileId)
			if (abortController.signal.aborted) {
				return
			}
			const url = URL.createObjectURL(blob)
			setPdfPreview({
				open: true,
				url,
				title: 'Brag Sheet',
			})
		} catch (error) {
			if (abortController.signal.aborted || error?.name === 'CanceledError') {
				return
			}
			setPdfPreview(current => {
				if (current.url) URL.revokeObjectURL(current.url)
				return { open: false, url: '', title: '' }
			})
			setComposerVisible(true)
			setToast({
				isVisible: true,
				message: error?.response?.data?.error || 'Failed to open Brag Sheet preview',
				type: 'error',
			})
		} finally {
			if (previewAbortRef.current === abortController) {
				previewAbortRef.current = null
			}
			if (!abortController.signal.aborted) {
				setIsPreviewLoading(false)
			}
		}
	}, [bragSheet?.fileId, isPreviewLoading, studentId])

	const pollJob = useCallback(
		async (nextLetterId, nextJobId) => {
			if (pollAbortRef.current) pollAbortRef.current.abort()
			const abortController = new AbortController()
			pollAbortRef.current = abortController
			let delay = INITIAL_POLL_MS

			try {
				while (!abortController.signal.aborted) {
					const job = await fetchCounselorLorJob(
						studentId,
						nextLetterId,
						nextJobId,
						{ signal: abortController.signal },
					)

					if (job.status === 'completed') {
						const letterResponse =
							job.letter ||
							(
								await fetchCounselorLorLetter(studentId, nextLetterId, {
									signal: abortController.signal,
								})
							)?.letter
						setLetter(letterResponse)
						setLetterId(nextLetterId)
						setJobId(nextJobId)
						setWritingStatus('success')
						setDrawerMode('view')
						onLetterSaved?.(letterResponse)
						return
					}

					if (job.status === 'failed') {
						setGenerationError(
							job?.error?.message ||
								"We couldn't generate this letter. Please try again.",
						)
						setWritingStatus('error')
						return
					}

					await wait(delay, abortController.signal)
					delay = Math.min(Math.round(delay * 1.25), MAX_POLL_MS)
				}
			} catch (error) {
				if (error?.name === 'AbortError' || error?.name === 'CanceledError') return
				setGenerationError(
					error?.response?.data?.error ||
						error?.message ||
						'Failed to check generation status',
				)
				setWritingStatus('error')
			}
		},
		[onLetterSaved, studentId],
	)

	useEffect(() => {
		if (writingStatus !== 'writing' || !letterId || !jobId || !studentId) return undefined
		pollJob(letterId, jobId)
		return () => {
			if (pollAbortRef.current) {
				pollAbortRef.current.abort()
				pollAbortRef.current = null
			}
		}
	}, [writingStatus, letterId, jobId, studentId, pollJob])

	const buildPayload = useCallback(() => {
		const payload = {
			mode,
			applicationTargetId,
			instructions,
			tones,
			highlights,
			useBragSheet: Boolean(bragSheet.available && useBragSheet),
			useProfileBuilder: Boolean(
				!bragSheet.available && profileBuilder.usable && useProfileBuilder,
			),
			referenceLetterId: selectedReferenceId || null,
		}
		if (mode === 'improve') {
			payload.letterId = letterId
			payload.expectedRevision = letter?.revision
		}
		return payload
	}, [
		applicationTargetId,
		bragSheet.available,
		highlights,
		instructions,
		letter?.revision,
		letterId,
		mode,
		profileBuilder.usable,
		selectedReferenceId,
		tones,
		useBragSheet,
		useProfileBuilder,
	])

	const generate = useCallback(async () => {
		if (!studentId || !applicationTargetId || writingStatus === 'writing') return
		if (!idempotencyKeyRef.current) {
			idempotencyKeyRef.current = createIdempotencyKey()
		}

		setWritingStatus('writing')
		setGenerationError('')
		setComposerVisible(true)
		setIsAppPopoverOpen(false)

		try {
			const result = await createCounselorLorGeneration(
				studentId,
				buildPayload(),
				{ idempotencyKey: idempotencyKeyRef.current },
			)
			setLetterId(result.letterId)
			setJobId(result.jobId)
		} catch (error) {
			setGenerationError(
				error?.response?.data?.error ||
					error?.message ||
					'Failed to start generation',
			)
			setWritingStatus('error')
		}
	}, [applicationTargetId, buildPayload, studentId, writingStatus])

	const stopWriting = useCallback(() => {
		if (pollAbortRef.current) {
			pollAbortRef.current.abort()
			pollAbortRef.current = null
		}
		setWritingStatus('idle')
		setGenerationError('')
		idempotencyKeyRef.current = null
	}, [])

	const retryGeneration = useCallback(async () => {
		if (!studentId || !letterId || !jobId) {
			idempotencyKeyRef.current = createIdempotencyKey()
			await generate()
			return
		}
		setWritingStatus('writing')
		setGenerationError('')
		try {
			const result = await retryCounselorLorJob(studentId, letterId, jobId)
			setJobId(result.jobId)
		} catch (error) {
			setGenerationError(
				error?.response?.data?.error || error?.message || 'Retry failed',
			)
			setWritingStatus('error')
		}
	}, [generate, jobId, letterId, studentId])

	const editResult = useCallback(() => {
		setDrawerMode('edit')
	}, [])

	const improveResult = useCallback(async () => {
		if (!letter) return
		if (editorFlushRef.current) {
			await editorFlushRef.current()
		}
		setMode('improve')
		setWritingStatus('idle')
		setDrawerMode('view')
		setComposerVisible(true)
		idempotencyKeyRef.current = null
		await loadContext({
			studentId,
			applicationTargetId: letter.applicationTargetId || applicationTargetId,
			excludeLetterId: letter.id,
			prefillSettings: letter.lastSettings || {},
		})
		setLetterId(letter.id)
	}, [applicationTargetId, letter, loadContext, studentId])

	const copyResult = useCallback(async () => {
		const text = String(letter?.contentText || '').trim()
		if (!text) return
		try {
			await navigator.clipboard.writeText(text)
			setToast({
				isVisible: true,
				message: 'Copied to clipboard',
				type: 'success',
			})
		} catch {
			setToast({
				isVisible: true,
				message: 'Could not copy letter',
				type: 'error',
			})
		}
	}, [letter?.contentText])

	const downloadResult = useCallback(async () => {
		if (!studentId || !letterId) return
		if (editorFlushRef.current) {
			await editorFlushRef.current()
		}
		try {
			const blob = await downloadCounselorLorPdf(studentId, letterId)
			const url = URL.createObjectURL(blob)
			const anchor = document.createElement('a')
			anchor.href = url
			anchor.download = `LoR_${selectedApplication?.studentName || 'letter'}.pdf`
			document.body.appendChild(anchor)
			anchor.click()
			anchor.remove()
			URL.revokeObjectURL(url)
		} catch (error) {
			setToast({
				isVisible: true,
				message: error?.response?.data?.error || 'Failed to download PDF',
				type: 'error',
			})
		}
	}, [letterId, selectedApplication?.studentName, studentId])

	const saveLetterSnapshot = useCallback(
		async snapshot => {
			if (!studentId || !letterId || !letter) return null
			const response = await patchCounselorLorLetter(studentId, letterId, {
				expectedRevision: letter.revision,
				title: letter.title,
				contentJson: snapshot.contentJson,
				contentHtml: snapshot.contentHtml,
				contentText: snapshot.contentText,
				wordCount: snapshot.wordCount,
			})
			setLetter(response.letter)
			setSavedPillVisible(true)
			window.setTimeout(() => setSavedPillVisible(false), 1000)
			onLetterSaved?.(response.letter)
			return response.letter
		},
		[letter, letterId, onLetterSaved, studentId],
	)

	const registerEditorFlush = useCallback(flush => {
		editorFlushRef.current = flush
	}, [])

	const closeToast = useCallback(() => {
		setToast(current => ({ ...current, isVisible: false }))
	}, [])

	const visibleReferences = showAllReferences
		? references
		: references.slice(0, 2)

	const canChangeApplication = mode !== 'improve'
	const isComposerOpen = isOpen && composerVisible && writingStatus === 'idle'
	const isWritingOpen = isOpen && composerVisible && writingStatus !== 'idle' && writingStatus !== 'success'
	const isResultOpen = isOpen && writingStatus === 'success'

	return {
		selectedApplication,
		contextStatus,
		contextError,
		instructions,
		tones,
		highlights,
		tonesList,
		highlightsList,
		showAllHighlights,
		showAllReferences,
		useBragSheet,
		useProfileBuilder,
		selectedReferenceId,
		references,
		visibleReferences,
		bragSheet,
		profileBuilder,
		applications,
		isAppPopoverOpen,
		pdfPreview,
		isPreviewLoading,
		mode,
		letter,
		writingStatus,
		generationError,
		drawerMode,
		savedPillVisible,
		toast,
		canChangeApplication,
		isComposerOpen,
		isWritingOpen,
		isResultOpen,
		changeInstructions,
		toggleTone,
		toggleHighlight,
		toggleHighlightsVisibility,
		toggleReferencesVisibility,
		toggleBragSheet,
		toggleProfileBuilder,
		selectReference,
		stopPropagation,
		changeApplication,
		selectApplication,
		closeAppPopover: () => setIsAppPopoverOpen(false),
		preview,
		closePdfPreview,
		generate,
		stopWriting,
		retryGeneration,
		editResult,
		improveResult,
		copyResult,
		downloadResult,
		saveLetterSnapshot,
		registerEditorFlush,
		setDrawerMode,
		closeToast,
		reloadContext: () =>
			loadContext({
				studentId,
				applicationTargetId,
				preserveSelections: true,
				excludeLetterId: mode === 'improve' ? letterId : null,
				prefillSettings: mode === 'improve' ? letter?.lastSettings : null,
			}),
	}
}
